// Week 1 / Day 4 — Small Expense Traps: Fees, Subscriptions and Overspending.
// Reworked: bilingual RU/EN inline; playful Nimny mission tone;
// 50% online + 50% paper/parent. Five layers preserved.
// SAFETY: fictional fees and subscriptions; no real services, no card entry.

const day4 = {
  id: 'day-4',
  studentId: 'max',
  weekId: 'week-1',
  dayId: 'day-4',
  status: 'active',
  title: 'Ловушки маленьких расходов: комиссии, подписки и перерасход / Small Expense Traps: Fees, Subscriptions and Overspending',
  shortTitle: 'Маленькие расходы / Small Expenses',
  theme:
    'Маленькие повторяющиеся расходы могут стать большими, если их не замечать. / Small repeated expenses can become big if we do not notice them.',
  estimatedTimeLabel:
    'Онлайн-миссия: около 20–30 минут. Бумажная миссия с родителем: около 25–35 минут. / Online mission: about 20–30 minutes. Paper mission with parent: about 25–35 minutes.',
  nimnyIntro:
    'Нимни нашла сундук маленьких расходов. Один ним здесь, два нима там — и баланс меняется. Помоги Нимни найти повторяющиеся расходы и не попасть в ловушку перерасхода. / Nimny found a chest of small expenses. One nim here, two nims there — and the balance changes. Help Nimny find repeated expenses and avoid overspending.',
  goals: [
    'Слой 1. Математика: считать комиссии и подписки за несколько недель, находить разницу при перерасходе. / Layer 1. Math: count fees and subscriptions across several weeks; find the gap when there is overspending.',
    'Слой 2. Русский язык и правописание: словарь расходов, мини-диктант, «не» с глаголами. / Layer 2. Russian language and spelling: expense vocabulary, mini-dictation, "не" with verbs.',
    'Слой 3. Чтение, письмо и пересказ: «Почему маленькие расходы важны?» — план, пересказ и 3–5 предложений. / Layer 3. Reading, writing and retelling: "Why do small expenses matter?" — plan, retell and 3–5 sentences.',
    'Слой 4. Наблюдение и логика — как повторение меняет результат. / Layer 4. Observation and Logic — How Repetition Changes the Result.',
    'Слой 5. Практика на бумаге: «Сундук маленьких расходов» и правило безопасного выбора. / Layer 5. Paper practice: "Small Expenses Chest" and a safe-choice rule.',
    'Дневник мышления: одна находка про повторяющиеся расходы. / Thinking diary: one insight about repeated expenses.',
    'Награда нимами: базово 3 нима, максимум 5 с бонусами. / Reward in nims: 3 base, up to 5 with bonuses.'
  ],
  vocabulary: [
    { term: 'комиссия / fee', definition: 'маленькая регулярная сумма за услугу. / a small regular amount for a service.' },
    { term: 'подписка / subscription', definition: 'расход, который повторяется каждую неделю или месяц. / an expense that repeats each week or month.' },
    { term: 'расход / expense', definition: 'нимы, которые ушли. / nims that went out.' },
    { term: 'регулярный / regular', definition: 'который повторяется по расписанию. / something that repeats on a schedule.' },
    { term: 'повторяться / repeat', definition: 'случаться снова и снова. / to happen again and again.' },
    { term: 'перерасход / overspending', definition: 'когда хочешь потратить больше, чем есть на счёте. / wanting to spend more than is in the account.' },
    { term: 'проверить / check', definition: 'посмотреть условия и сумму. / look at the terms and the total.' },
    { term: 'отказаться / say no', definition: 'выбрать не оформлять. / choose not to take it.' },
    { term: 'условие / condition', definition: 'правило, по которому работает подписка. / the rule under which a subscription works.' },
    { term: 'взрослый / adult', definition: 'тот, кого спрашивают перед покупкой. / someone we ask before a purchase.' }
  ],
  onlineTasks: [
    {
      id: 'q1',
      type: 'math',
      title: 'Десятичная подписка / Decimal subscription',
      prompt:
        'Подписка стоит 7.50 нимов в месяц. Сколько за 6 месяцев? / A subscription costs 7.50 nims per month. How much for 6 months?',
      helper:
        'Что сделать: умножь 7.50 на 6. Ответ — десятичная дробь или целое число. / What to do: multiply 7.50 × 6.',
      answer: '45',
      unit: 'нимов / nims'
    },
    {
      id: 'q2',
      type: 'math',
      title: 'Линейная модель / Linear plan',
      prompt:
        'План A: 12 нимов сразу + 5 нимов каждый месяц. Сколько за 8 месяцев? / Plan A: 12 nims up front + 5 nims per month. Total after 8 months?',
      helper:
        'Что сделать: 12 + 5 × 8. Сначала умножение, потом сложение. / What to do: 12 + 5 × 8. Multiply first, then add.',
      answer: '52',
      unit: 'нимов / nims'
    },
    {
      id: 'q3',
      type: 'math',
      title: 'Скидка / Discount',
      prompt:
        'Предмет стоил 80 нимов. Скидка 25%. Новая цена? / Item costs 80 nims. 25% discount. New price?',
      helper:
        'Что сделать: 1) найди 25% от 80; 2) вычти из 80. / What to do: 1) find 25% of 80; 2) subtract from 80.',
      answer: '60',
      unit: 'нимов / nims'
    },
    {
      id: 'q4',
      type: 'math',
      title: 'Таблица расходов / Expenses table',
      prompt:
        'Три маленьких расхода в месяц: 4, 7 и 12 нимов. Сколько всего за 5 месяцев? / Three small monthly expenses: 4, 7 and 12 nims. Total over 5 months?',
      helper:
        'Что сделать: сложи расходы за месяц, потом умножь на 5. / What to do: sum the monthly expenses, then multiply by 5.',
      answer: '115',
      unit: 'нимов / nims'
    },
    {
      id: 'q_compare',
      type: 'math',
      title: 'Сравнение планов / Compare plans',
      prompt:
        'Сравни за 12 месяцев: План A — подписка 7.50 нима/мес. План B — 12 нимов сразу + 5 нимов/мес. Какой план меньше и на сколько нимов? / Compare over 12 months: A — 7.50/mo subscription; B — 12 nims up front + 5/mo. Which is smaller, and by how many nims?',
      helper:
        'Что сделать: A = 7.50 × 12; B = 12 + 5 × 12; сравни и найди разницу. / What to do: A = 7.50 × 12; B = 12 + 5 × 12; compare and find the difference.',
      answer: '18',
      unit: 'нимов / nims'
    },
    {
      id: 'q_s1',
      type: 'math',
      title: 'Растяжка — повторение во времени / Stretch — repeat over time',
      prompt:
        'Маленький расход 5 нимов повторяется каждый месяц. Сколько всего за 6 месяцев? / A 5-nim expense repeats each month. Total over 6 months?',
      helper:
        '5 × 6. Что меняется: общая сумма растёт линейно с числом повторений. / 5 × 6. The total grows linearly with repeats.',
      answer: '30',
      unit: 'нимов / nims'
    },
    {
      id: 'q5',
      type: 'classify',
      title: 'Правописание «не» с глаголами и слова расходов / "Не" with verbs and expense words',
      prompt:
        'Как пишется «не» с глаголом? И какая буква в корне у слов расходов? / How is "не" written with a verb? Which letter is in the root of expense words?',
      categories: [
        { id: 'separate', label: '«не» с глаголом — раздельно / "не" + verb — separately' },
        { id: 'root_a', label: 'безударная «а» в корне / unstressed "а"' },
        { id: 'root_o', label: 'безударная «о» в корне / unstressed "о"' }
      ],
      items: [
        { id: 'ne_proverit', text: '(не)проверить условия / "(не)проверить"' },
        { id: 'ne_otkazatsya', text: '(не)отказаться от подписки / "(не)отказаться"' },
        { id: 'ne_speshit', text: '(не)спешить с выбором / "(не)спешить"' },
        { id: 'ras_hod', text: 'р_сход — проверка: расходы / "raskhod" — check: расходы' },
        { id: 'pover_it', text: 'пр_верить — проверка: проверка / "proverit" — check: проверка' },
        { id: 'sov_et', text: 'с_вет — проверка: советы / "sovet" — check: советы' }
      ]
    },
    {
      id: 'q6',
      type: 'text',
      title: 'Мини-диктант / Mini-dictation',
      prompt:
        'Запиши без ошибок: «Регулярные расходы легко не заметить. Перед покупкой или подпиской нужно спросить взрослого.» / Write without mistakes: "Regular expenses are easy to miss. Before a purchase or subscription, ask an adult."',
      placeholder:
        'Регулярные расходы ... / Regular expenses ...'
    },
    {
      id: 'q_corr',
      type: 'text',
      title: 'Исправь ошибки / Correct the mistakes',
      prompt:
        'Перепиши без ошибок: «регулярные расхады лекко незаметить, перед покупкой или подпискай нужно спросит взрослого.» Подсказки: «расходы», «легко» — безударные гласные; «не заметить» — «не» с глаголом раздельно; «подпиской» — окончание «-ой»; «спросить» — мягкий знак; заглавная буква и точка. / Rewrite: fix unstressed vowels; «не» + verb separate; ending; soft sign; capital and period.',
      placeholder:
        'Регулярные расходы легко не заметить. Перед покупкой или подпиской нужно спросить взрослого. / Regular expenses ...'
    },
    {
      id: 'q7',
      type: 'text',
      title: 'План текста и письмо / Text plan and writing',
      prompt:
        'Прочитай короткий текст: «Маленький расход кажется незаметным. Но если он повторяется каждую неделю, за месяц он становится большим. Поэтому перед подпиской мы проверяем условия и спрашиваем взрослого.» Запиши план из 3 пунктов и 3–5 предложений на русском по теме: «Почему нужно проверять повторяющиеся расходы?» / Read the short text: "A small expense feels invisible. But if it repeats every week, it becomes big in a month. That is why we check the terms and ask an adult before a subscription." Write a 3-point plan and 3–5 Russian sentences.',
      placeholder:
        '1. ... 2. ... 3. ... Мои предложения: ... / 1. ... 2. ... 3. ... My sentences: ...'
    },
    {
      id: 'q_obs',
      type: 'text',
      title: 'Наблюдение и логика — повторение меняет итог / Observation and Logic — Repetition Changes the Total',
      prompt:
        'Реши коротко:\n1 повторение по 5 нимов: итог = ____ нимов\n2 повторения по 5 нимов: итог = ____ нимов\n4 повторения по 5 нимов: итог = ____ нимов\nЧто меняется при повторении? ____\nЧто остаётся одинаковым (правило)? ____\nЧто происходит при «перерасходе»? ____\nСделай вывод одной фразой. / Compute 1/2/4 repeats of 5 nims; what changes vs what stays the same; what is overspending; one-line conclusion.',
      placeholder:
        '1 раз: ... 2 раза: ... 4 раза: ... Меняется: ... Одинаково: ... Перерасход: ... Вывод: ... / 1×: ... 2×: ... 4×: ... Changes: ... Same: ... Overspending: ... Conclusion: ...'
    },
    {
      id: 'q8',
      type: 'classify',
      title: 'Сундук маленьких расходов / Small Expenses Chest',
      prompt:
        'Распредели карточки по 4 корзинам. / Sort the cards into 4 baskets.',
      categories: [
        { id: 'one', label: 'Разовый расход / One-time expense' },
        { id: 'reg', label: 'Регулярный расход / Regular expense' },
        { id: 'ask', label: 'Нужно спросить взрослого / Ask an adult' },
        { id: 'short', label: 'Не хватает нимов / Not enough nims' }
      ],
      items: [
        { id: 'fivenims', text: '5 нимов один раз / 5 nims once' },
        { id: 'feemonth', text: '3 нима каждый месяц / 3 nims each month' },
        { id: 'sub', text: 'подписка 5 нимов в неделю / subscription 5 nims/week' },
        { id: 'strangebtn', text: 'странная кнопка «купить» / a strange "buy" button' },
        { id: 'short5', text: '30 есть, нужно 35 / 30 in, 35 needed' },
        { id: 'asknext', text: 'сначала спросить родителя / first ask a parent' },
        { id: 'readterms', text: 'проверить условия / check the conditions' }
      ]
    },
    {
      id: 'q9',
      type: 'essay',
      title: 'Дневник мышления: правило безопасного выбора / Thinking diary: safe-choice rule',
      prompt:
        'Напиши 3–5 предложений: какое правило поможет не попасть в ловушку маленьких расходов и почему. Используй слова: подписка, комиссия, проверить, спросить взрослого. / Write 3–5 sentences: what rule will help avoid the small-expense trap and why. Use words: subscription, fee, check, ask an adult.',
      minSentences: 3,
      maxSentences: 5,
      placeholder:
        'Моё правило ... / My rule ...'
    }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 1, День 4 — Сундук маленьких расходов». / Heading: "Week 1, Day 4 — Small Expenses Chest".',
    'Слой 1. Математика: реши q1–q4 и нарисуй короткую полосу 4 повторяющихся расходов. / Layer 1. Math: solve q1–q4 and draw a short bar of 4 repeated expenses.',
    'Слой 2. Русский язык: запиши словарь расходов и мини-диктант. / Layer 2. Russian: write the expense vocabulary and the mini-dictation.',
    'Слой 3. Чтение и пересказ: 3-пунктовый план + 3–5 предложений; перескажи родителю. / Layer 3. Reading and retelling: 3-point plan + 3–5 sentences; retell to a parent.',
    'Слой 4. Наблюдение и логика: запиши, как меняется итог при 1, 2 и 4 повторениях. / Layer 4. Observation and Logic: write how the total changes for 1, 2 and 4 repetitions.',
    'Слой 5. Практика: разложи сундук маленьких расходов и запиши правило безопасного выбора (5 шагов). / Layer 5. Practice: sort the chest and write a 5-step safe-choice rule.',
    'Дневник мышления: одна находка про повторяющиеся расходы. / Thinking diary: one repeated-expense insight.',
    'Награда нимами начисляется после проверки родителем. / Reward in nims is added after a parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 1, День 4 / Week 1, Day 4',
    subtitle: 'Сундук маленьких расходов / Small Expenses Chest',
    pages: [
      {
        title: 'Маршрут миссии — сундук маленьких расходов / Mission Route — Small Expenses Chest',
        blocks: [
          { type: 'text', text: 'Сегодня — пять слоёв и одна миссия. Отмечай ☐ → ☑. / Today — five layers and one mission. Mark ☐ → ☑.' },
          { type: 'list', items: [
            '☐ Слой 1. Математика — около 10 минут. / Layer 1. Math — about 10 minutes.',
            '☐ Слой 2. Русский язык — около 10 минут. / Layer 2. Russian — about 10 minutes.',
            '☐ Слой 3. Чтение и пересказ — около 10 минут. / Layer 3. Reading and retelling — about 10 minutes.',
            '☐ Слой 4. Наблюдение и логика — около 5 минут. / Layer 4. Observation and Logic — about 5 minutes.',
            '☐ Слой 5. Практика на бумаге — около 15 минут. / Layer 5. Paper practice — about 15 minutes.',
            '☐ Дневник мышления — 5 минут. / Thinking diary — 5 minutes.',
            '☐ Печать миссии и награда нимами. / Mission stamp and nim reward.'
          ]}
        ]
      },
      {
        title: 'Слой 1. Математика — подписки, линейный план и скидка / Layer 1. Math — Subscriptions, Linear Plan and Discount',
        blocks: [
          { type: 'text', text: 'Считай в нимах. Записывай шаги. / Count in nims. Show your steps.' },
          { type: 'list', items: [
            '1) Десятичная подписка: 7.50 × 6 = ____ нимов / nims (за 6 месяцев).',
            '2) Линейный план: 12 + 5 × 8 = ____ нимов / nims (порядок: сначала ×, потом +).',
            '3) Скидка 25% от 80: 25% × 80 = ____ . Новая цена = 80 − ____ = ____ нимов / nims.',
            '4) Таблица расходов: (4 + 7 + 12) × 5 = ____ × 5 = ____ нимов / nims за 5 месяцев.',
            '5) Сравни планы за 12 месяцев: A) 7.50 × 12 = ____ . B) 12 + 5 × 12 = ____ . Что меньше? ____',
            '6) Растяжка: рост повторений — закрась ▮ за каждые 5 нимов до 30. / Stretch — repeat bar:',
            '   Месяц 1: ▮▮▮▮▮▮ → ____ нимов',
            '   Месяц 3: ▮▮▮▮▮▮ × 3 = ____ нимов',
            '   Месяц 6: ▮▮▮▮▮▮ × 6 = ____ нимов'
          ]}
        ]
      },
      {
        title: 'Слой 2. Русский язык и правописание — слова расходов / Layer 2. Russian Language and Spelling — Expense Words',
        blocks: [
          { type: 'text', text: 'Правило дня: «не» с глаголами пишется раздельно (не проверить, не отказаться). / Rule of the day: "не" with verbs is written separately.' },
          { type: 'list', items: [
            'Словарь миссии: комиссия, подписка, расход, регулярный, повторяться, перерасход, проверить, отказаться, условие, взрослый. / Mission vocabulary.',
            'Мини-диктант: «Регулярные расходы легко не заметить. Перед покупкой или подпиской нужно спросить взрослого.» / Mini-dictation.',
            'Запиши без ошибок: __________________________________________ / Write without mistakes:',
            'Исправление: «регулярные расхады лекко незаметить, перед покупкой или подпискай нужно спросит взрослого.» / Correct and rewrite the sentence with mistakes.',
            'Подчеркни «не» + глагол отдельно: не проверить, не отказаться, не спешить. / Underline "не" + verb (separately).'
          ]}
        ]
      },
      {
        title: 'Слой 3. Чтение, письмо и пересказ / Layer 3. Reading, Writing and Retelling',
        blocks: [
          { type: 'text', text: 'Прочитай короткий текст и сделай план. / Read the short text and make a plan.' },
          { type: 'list', items: [
            'Текст: «Маленький расход кажется незаметным. Но если он повторяется каждую неделю, за месяц он становится большим. Поэтому перед подпиской мы проверяем условия и спрашиваем взрослого.» / Text: "A small expense feels invisible..."',
            'План: 1) ____________________  2) ____________________  3) ____________________ / Plan',
            'Главная мысль: ______________________________________ / Main idea',
            'Мои 3–5 предложений «Почему нужно проверять повторяющиеся расходы?»: / My 3–5 sentences',
            '1. ______________________________________________',
            '2. ______________________________________________',
            '3. ______________________________________________',
            '☐ Я пересказал родителю / I retold a parent.'
          ]}
        ]
      },
      {
        title: 'Слой 4. Наблюдение и логика — повторение меняет результат / Layer 4. Observation and Logic — Repetition Changes the Result',
        blocks: [
          { type: 'text', text: 'Понаблюдай: один маленький расход и много маленьких расходов — это разные суммы. / Observe: one small expense vs many small expenses are different totals.' },
          { type: 'list', items: [
            '1 повторение по 5 нимов: итог = ____ нимов / 1 repeat of 5: total = ____ nims',
            '2 повторения по 5 нимов: итог = ____ нимов / 2 repeats of 5: total = ____ nims',
            '4 повторения по 5 нимов: итог = ____ нимов / 4 repeats of 5: total = ____ nims',
            'Что меняется при повторении? ____________________ / What changes with repetition?',
            'Что остаётся одинаковым? ____________________ / What stays the same?',
            'Что происходит при «перерасходе»? ____________________ / What happens with overspending?',
            'Мой вывод (1 фраза): ____________________ / My conclusion (1 line):'
          ]}
        ]
      },
      {
        title: 'Слой 5. Практика на бумаге — правило безопасного выбора / Layer 5. Paper Practice — Safe-Choice Rule',
        blocks: [
          { type: 'text', text: 'Нарежь или отметь карточки. Разложи их в 4 корзины. / Cut or mark the cards. Sort into 4 baskets.' },
          { type: 'list', items: [
            'Корзины: «Разовый расход», «Регулярный расход», «Спросить взрослого», «Не хватает нимов». / Baskets.',
            'Карточки: 5 нимов один раз; 3 нима каждый месяц; подписка 5 нимов/нед; странная кнопка «купить»; 30 есть, нужно 35; сначала спросить родителя; проверить условия. / Cards.',
            '☐ Обведи 1 регулярный расход. / Circle 1 regular expense.',
            '☐ Обведи 1 «спросить взрослого». / Circle 1 "ask an adult".',
            '☐ Обведи 1 «не хватает нимов». / Circle 1 "not enough".',
            'Моё правило безопасного выбора (5 шагов): / My 5-step safe-choice rule:',
            '1) Проверяю цену. / Check the price.',
            '2) Проверяю, повторяется ли расход. / Check if it repeats.',
            '3) Спрашиваю взрослого. / Ask an adult.',
            '4) Не ввожу данные карты. / Do not enter card details.',
            '5) Останавливаюсь, если не уверен. / Stop if unsure.',
            'Моя подпись правила: ____________________ / My signature on the rule:'
          ]}
        ]
      },
      {
        title: 'Дневник мышления / Thinking Diary',
        blocks: [
          { type: 'list', items: [
            'Сегодня я заметил повторяющийся расход: ______________________ / Today I noticed a repeated expense',
            'Это важно, потому что _________________________ / This matters because',
            'Слово миссии, которое запомнилось: ________________ / Mission word I remember'
          ]}
        ]
      },
      {
        title: 'Родительская проверка и награда нимами / Parent Check and Nim Reward',
        blocks: [
          { type: 'text', text: 'После проверки начисли бонусные нимы через Parent Room. / After checking, add bonus nims through Parent Room.' },
          { type: 'list', items: [
            'Базовая награда: 3 нима ☐ / Base reward: 3 nims ☐',
            'Бонус за точный расчёт повторяющихся расходов: +1 ☐ / Repeated-expense-table bonus: +1 ☐',
            'Бонус за понятное правило безопасного выбора: +1 ☐ / Safe-spending-rule bonus: +1 ☐',
            'Итого: ____ из 5 нимов. / Total ____ of 5 nims.',
            'Печать миссии (родитель): ☐ / Mission stamp (parent): ☐',
            'Подпись родителя: ____________________ / Parent signature'
          ]}
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Показать, что маленькие повторяющиеся расходы складываются в большие, и научить простому правилу безопасного выбора. Никаких реальных подписок и карт. / Show that small repeated expenses add up; teach a simple safe-choice rule. No real subscriptions or cards.',
    mathGoal:
      'Десятичная подписка (7.50 × 6 = 45), линейный план (12 + 5 × 8 = 52), процентная скидка (25% × 80 → 60) и таблица повторяющихся расходов ((4 + 7 + 12) × 5 = 115). / Decimal subscription, linear plan, percent discount, repeating-expenses table.',
    russianGoal:
      'Раздельное «не» с глаголами; правильно записанный мини-диктант; исправлены 3–5 коротких предложений. / "не" + verb separately; correct mini-dictation; 3–5 sentences corrected.',
    readingWritingGoal:
      'План из 3 пунктов; пересказ родителю; 3–5 предложений на русском о проверке повторяющихся расходов. / 3-point plan; retell; 3–5 Russian sentences on checking repeated expenses.',
    practicalGoal:
      'Карточки разложены в 4 корзины (разовый/регулярный/спросить/не хватает); записано правило безопасного выбора из 5 шагов. / Cards sorted into 4 baskets; 5-step safe-choice rule written.',
    observationGoal:
      'Ребёнок видит, что итог растёт при повторениях; «перерасход» появляется, когда хочешь больше, чем есть на счёте. / Child sees that the total grows with repeats; overspending appears when wanting more than is on the account.',
    check: [
      'Q1: 45 нимов (7.50 × 6). Q2: 52 нима (12 + 5×8). Q3: 60 нимов (80 − 20). Q4: 115 нимов ((4+7+12) × 5). / Q1: 45. Q2: 52. Q3: 60. Q4: 115.',
      'Мини-диктант записан без грубых ошибок. / Mini-dictation OK.',
      'План текста — 3 пункта; пересказ — 3–5 предложений по-русски. / Plan = 3 points; retell = 3–5 Russian sentences.',
      'Сундук разложен по 4 корзинам; обведены 1 регулярный, 1 «спросить», 1 «не хватает». / Chest sorted; required cards circled.',
      'Правило безопасного выбора записано из 5 шагов. / 5-step rule written.',
      'Дневник мышления заполнен. / Thinking diary filled.'
    ],
    answerKey: [
      { taskId: 'q1', answer: 'Answer: 45 нимов. Solution: 7.50 × 6 = 45 (или 7 × 6 = 42, 0.50 × 6 = 3, 42 + 3 = 45). Parent note: десятичная часть × количество — это ещё одно сложение. / 7.50 × 6 = 45.' },
      { taskId: 'q2', answer: 'Answer: 52 нима. Solution: 5 × 8 = 40; 12 + 40 = 52. Parent note: порядок действий — сначала умножение, потом сложение. / 12 + 5×8 = 52.' },
      { taskId: 'q3', answer: 'Answer: 60 нимов. Solution: 25% × 80 = 20; 80 − 20 = 60. Parent note: 25% — это 1/4, можно делить вместо умножения. Проверка: 60 + 20 = 80. / 25% × 80 = 20, new = 60.' },
      { taskId: 'q4', answer: 'Answer: 115 нимов. Solution: 4 + 7 + 12 = 23; 23 × 5 = 115. Parent note: модель повторения — маленькие суммы складываем и умножаем на число месяцев. / (4+7+12) × 5 = 115.' },
      { taskId: 'q_compare', answer: 'Answer: План B меньше на 18 нимов. Solution: A = 7.50 × 12 = 90; B = 12 + 5 × 12 = 12 + 60 = 72; A − B = 18. Parent note: «дешевле сразу + меньше в месяц» часто оказывается выгоднее «без входной платы». / Compare: A=90, B=72, B smaller by 18.' },
      { taskId: 'q_s1', answer: 'Answer: 30 нимов. Solution: 5 × 6 = 30. Parent note: линейный рост — итог = (расход за период) × (количество периодов). / 5 × 6 = 30.' },
      { taskId: 'q5', answer: 'Answer: «не» с глаголами раздельно — не проверить, не отказаться, не спешить. Безударные: расход — а (расходы); проверить — о (проверка); совет — о (советы). Solution: «не» с глаголами почти всегда раздельно; безударные — проверочное слово с ударением. Parent note: исключения («ненавидеть», «недоумевать») в этом возрасте не разбираем. / "не" separate + vowels.' },
      { taskId: 'q6', answer: 'Answer: «Регулярные расходы легко не заметить. Перед покупкой или подпиской нужно спросить взрослого.» Solution: «не заметить» — раздельно; точки и заглавные. Parent note: ≤ 2 ошибок — нормально. / Mini-dictation: ≤ 2 errors acceptable.' },
      { taskId: 'q_corr', answer: 'Answer: «Регулярные расходы легко не заметить. Перед покупкой или подпиской нужно спросить взрослого.» Solution: регулярные→Регулярные (заглавная); расхады→расходы (проверка: расход); лекко→легко (проверка: лёгкий); незаметить→не заметить (раздельно); подпискай→подпиской (окончание -ой); спросит→спросить (мягкий знак). Parent note: 6 ошибок — засчитывай ≥ 5/6. / Acceptance: ≥ 5/6.' },
      { taskId: 'q7', answer: 'Answer: 3-пунктовый план + 3–5 предложений. Solution: пример плана — 1) маленький расход кажется незаметным; 2) повторение делает его большим; 3) проверь условия и спроси взрослого. Parent note: главное — связь «повторение → большая сумма». / 3-point plan; key idea: repetition.' },
      { taskId: 'q_obs', answer: 'Answer: 1×5 = 5; 2×5 = 10; 4×5 = 20. Меняется — итоговая сумма (линейный рост). Одинаково — единичный расход 5 и правило «итог = расход × число повторений». Перерасход — когда хочешь потратить больше, чем есть на счёте. Solution: вывод — «Маленькие расходы становятся большими, если их не считать». Parent note: принимай любой вывод про линейный рост или необходимость проверки. / Linear growth + overspending.' },
      { taskId: 'q8', answer: 'Answer: Разовый — 5 нимов один раз. Регулярный — 3 нима/мес, подписка 5/нед. Спросить взрослого — странная кнопка, сначала спросить родителя, проверить условия. Не хватает нимов — 30 есть, нужно 35. Solution: «проверить условия» можно отнести и к «спросить взрослого» (родитель помогает читать) — обе версии засчитывай. Parent note: проговорите спорное «проверить условия». / Sortings with reasoning.' },
      { taskId: 'q9', answer: 'Answer: открытый ответ. 3–5 предложений со словами: подписка, комиссия, проверить, спросить взрослого. Solution: пример — «Моё правило: перед подпиской я проверю, повторяется ли расход. Если есть комиссия, спрошу взрослого. Так маленький расход не вырастет.» Parent note: проверь, что все 4 слова использованы. / Open answer with required words.' }
    ],
    spellingCheck:
      'Раздельное «не» с глаголами в 3 из 3; правильно подобраны проверочные для безударных гласных хотя бы в 2 из 3 слов. / Separate "не" in 3/3; check words for at least 2/3 unstressed.',
    readingCheck:
      'План — 3 пункта; пересказ по-русски без чтения с листа. / 3-point plan; retell in Russian without reading.',
    practicalCheck:
      'Сундук разложен логично; 5 шагов правила записаны и понятны; ребёнок может объяснить шаги. / Chest sorted logically; 5 steps written and clear; child can explain steps.',
    observationCheck:
      'Ребёнок верно заполняет таблицу повторений (5 / 10 / 20) и называет, когда возникает перерасход. / Repeats table 5 / 10 / 20 correct; names when overspending appears.',
    discussionPrompts: [
      'Что такое регулярный расход? / What is a regular expense?',
      'Почему маленькие повторяющиеся расходы могут стать большими? / Why can small repeated expenses become big?',
      'Что нужно сделать перед тем, как нажать «купить» или «подписаться»? / What to do before clicking "buy" or "subscribe"?',
      'Что такое перерасход в этом уроке? / What does overspending mean here?'
    ],
    ifEasy:
      'Усложни: попроси посчитать комиссию 3 нима × 12 месяцев и подписку 4 нима × 8 недель и сравнить с разовой покупкой 50 нимов. / Ask the child to count 3 × 12 and 4 × 8 and compare with one-time 50 nims.',
    ifDifficult:
      'Возьми 6 одинаковых нимов-карточек и сложи их в стопки по 1, 2, 4. Считайте сумму вместе. / Use 6 equal nim cards; stack them in 1, 2, 4 and count the totals together.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'repeated-expense-table', label: 'Бонус за точный расчёт повторяющихся расходов', amount: 1 },
      { id: 'safe-spending-rule', label: 'Бонус за понятное правило безопасного выбора', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: ['q1', 'q5', 'q7', 'q8', 'q9'],
    allowNotebookCompletion: true
  },
  nextLessonId: 'max-week-1-day-5'
};

export default day4;
